import {
  ReactNodeViewRenderer,
  mergeAttributes,
  NodePos,
  Node,
} from '@tiptap/react';

import { Quote } from './quote';
import { QuoteCaption } from './quote-caption';
import { XingQuoteView } from './xing-quote-view';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    xingQuote: {
      toggleXingQuote: () => ReturnType;
    };
  }
}

export const XingQuote = Node.create({
  name: 'xingQuote',

  group: 'block',

  content: 'quote quoteCaption',

  isolating: true,

  addExtensions() {
    return [Quote, QuoteCaption];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: `node-${this.name}` }),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const nodePos = state.selection.$from;
        const nodeType = nodePos.node().type.name;

        if (nodeType !== 'quote') {
          return false;
        }

        const isAtEnd = nodePos.parentOffset === nodePos.parent.nodeSize - 2;

        if (!isAtEnd) {
          return false;
        }

        const myNodePos = new NodePos(nodePos, editor);
        const xingQuotePos = myNodePos.closest('xingQuote');

        // xingQuotePos will return both quote and quoteCaption, then to get the quoteCaption we use the lastChild
        const xingQuoteCaptionPos = xingQuotePos?.lastChild;
        const pos = xingQuoteCaptionPos?.pos ?? 0;
        const size = xingQuoteCaptionPos?.size ?? 0;

        return editor
          .chain()
          .selectNodeForward()
          .focus(pos + size - 2)
          .run();
      },

      Backspace: ({ editor }) => {
        const {
          state: {
            selection: { $from },
          },
        } = editor;

        if ($from.parent.type.name !== 'quote') {
          return false;
        }

        const node = $from.node();

        const hasEmptyContent = node.content.size === 0;

        if (!hasEmptyContent) {
          return false;
        }

        return editor.chain().toggleXingQuote().run();
      },
    };
  },

  addCommands() {
    return {
      toggleXingQuote:
        () =>
        ({ chain, editor }) => {
          const {
            state: {
              doc,
              selection: { $from },
            },
          } = editor;

          // get the current node type and check if it is active
          const currentNode = $from.node();
          const currentNodeType = currentNode?.type.name;
          const isActive = ['quote', 'quoteCaption', 'xingQuote'].includes(
            currentNodeType
          );

          // if is active, remove the quote and add paragraph with the quote content (discarding the quote caption)
          if (isActive) {
            const myNodePos = new NodePos($from, editor);
            const xingQuotePos = myNodePos.closest('xingQuote');
            const quoteNodePos = xingQuotePos?.firstChild;

            const quoteText = quoteNodePos?.textContent || '';

            const paragraphPos = xingQuotePos?.pos || 1;

            if (quoteText) {
              return chain()
                .deleteNode(this.type)
                .insertContentAt(paragraphPos - 1, {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: quoteText,
                    },
                  ],
                })
                .focus(paragraphPos + quoteText.length)
                .run();
            }

            return chain()
              .deleteNode(this.type)
              .insertContentAt(paragraphPos - 1 || 1, {
                type: 'paragraph',
              })
              .focus(paragraphPos)
              .run();
          }

          const startPosition = $from.start();
          const endPosition = $from.end();

          // if is not active, remove the current node and add a quote with the content of the current node
          const textBlock = doc.textBetween(startPosition, endPosition);

          return chain()
            .focus()
            .deleteNode(currentNodeType)
            .insertContentAt(startPosition - 1, {
              type: this.name,
              content: [
                {
                  type: 'quote',
                  content: textBlock
                    ? [
                        {
                          type: 'text',
                          text: textBlock,
                        },
                      ]
                    : undefined,
                },
                {
                  type: 'quoteCaption',
                },
              ],
            })
            .focus(endPosition + 1)
            .run();
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(XingQuoteView);
  },
});
